import React from 'react';
import { useVendorEmail, useSendMissingDocsEmail } from '../../../hooks/vendors';
import { useLocationsStore } from '../../../hooks/useLocationsStore';
import VendorStatementFormModal, {
  VendorStatementFormModalProps,
  VendorStatementFormData,
} from './VendorStatementFormModal';

interface VendorStatementFormModalContainerProps
  extends Omit<VendorStatementFormModalProps, 'onSubmit' | 'initialData' | 'isLoadingEmails' | 'isSending'> {
  /** Vendor ID for fetching emails */
  vendorId?: string;
  /** Vendor Name for email subject */
  vendorName: string;
  /** Account ID for missing docs email */
  accountId?: string;
  /** Callback when form is submitted */
  onSubmit?: (data: VendorStatementFormData) => Promise<void>;
  /** Initial form data (will be overridden by API data) */
  initialData?: Partial<VendorStatementFormData>;
}

/**
 * Container component that handles API integration for VendorStatementFormModal
 */
const VendorStatementFormModalContainer: React.FC<VendorStatementFormModalContainerProps> = ({
  vendorId,
  vendorName,
  accountId,
  onSubmit,
  initialData,
  ...modalProps
}) => {
  const currentLocation = useLocationsStore(state => state.currentLocation);
  const locationId = currentLocation?.id;

  const { data: vendorEmailData, isLoading: isLoadingEmails } = useVendorEmail({
    locationId,
    vendorId,
    enabled: modalProps.isOpen && !!locationId && !!vendorId,
  });

  const sendMissingDocsEmailMutation = useSendMissingDocsEmail();

  // Prepare initial data with API response
  const enhancedInitialData: Partial<VendorStatementFormData> = {
    ...initialData,
    id: vendorEmailData?.accountNumber || initialData?.id || accountId || '',
    sendTo: vendorEmailData?.contactEmail
      ? [
          {
            email: vendorEmailData.contactEmail,
            isValid: true,
            isConfirmed: true,
            isSelected: false,
          },
        ]
      : initialData?.sendTo || [],
  };

  const handleFormSubmit = async (data: VendorStatementFormData) => {
    if (onSubmit) {
      await onSubmit(data);
    } else if (locationId && accountId) {
      const selectedEmails = data.sendTo.filter(recipient => recipient.isSelected).map(recipient => recipient.email);

      await sendMissingDocsEmailMutation.mutateAsync({
        locationId,
        accountId,
        emails: selectedEmails,
        replyTo: data.replyTo,
        dryRun: false,
        vendorName,
      });
    } else {
      console.warn('Missing required parameters for sending email:', {
        locationId: !!locationId,
        accountId: !!accountId,
      });
    }
  };

  return (
    <VendorStatementFormModal
      {...modalProps}
      initialData={enhancedInitialData}
      isLoadingEmails={isLoadingEmails}
      isSending={sendMissingDocsEmailMutation.isPending}
      onSubmit={handleFormSubmit}
    />
  );
};

export default VendorStatementFormModalContainer;
